import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApplicationCaseAdvancedFilterOptions,
  BaseFilterOptions,
  CaseSearchResponseResult,
  ReferenceDataCategoriesConstants,
  ReferenceDataConstants,
  ReferenceDataService
} from 'rome-library';
import { BehaviorSubject, combineLatest, Observable, Subject, timer } from 'rxjs';
import { filter, map, share, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ExternalCaseSearchResultDto } from '../models/external-case-search-result-dto';
import { NotificationCheckDto } from '../models/notification-check-dto.model';
import { UnreadMessageDto } from '../models/unread-message-dto.model';

@Injectable({
  providedIn: 'root'
})
export class UserNotificationService {
  private readonly millisecondsInMinute = 60_000;
  private readonly maximumUnreadMessagesCases = 6;

  private readonly stop$ = new Subject();
  private readonly refresh$ = new BehaviorSubject({});
  private readonly numberOfAlertedCases$ = new BehaviorSubject(0);
  private notificationsAvailable$: Observable<NotificationCheckDto>;
  private latestCheckTime: string;
  private numberOfCasesAssigned = 0;
  private unreadMessages$: Observable<Array<UnreadMessageDto>>;

  constructor(private readonly referenceDataService: ReferenceDataService, private readonly httpClient: HttpClient) {}

  public initialize(): void {
    const notificationRefreshRate =
      +this.referenceDataService.getConfigurationValue(
        ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
        ReferenceDataConstants.MessageNotificationRefreshRate
      ) * this.millisecondsInMinute;

    this.latestCheckTime = undefined;

    this.notificationsAvailable$ = this.buildNotificationsAvailableObservable(notificationRefreshRate);

    this.unreadMessages$ = this.buildUnreadMessagesObservable();
  }

  public numberOfAlertedCasesAsObservable(): Observable<number> {
    return this.numberOfAlertedCases$.asObservable();
  }

  public unreadMessagesAsObservable(): Observable<Array<UnreadMessageDto>> {
    return this.unreadMessages$;
  }

  public refreshUnreadMessages(): void {
    this.refresh$.next({});
  }

  public stop(): void {
    this.stop$.next();
  }

  private isNotificationAvailable(latestRequestTime: string): Observable<NotificationCheckDto> {
    return this.httpClient
      .get('/ApplicationCase/IsNotificationAvailable', {
        params: {
          latestRequestTime
        }
      })
      .pipe(map((response) => new NotificationCheckDto().deserialize(response)));
  }

  private getUnreadMessages(): Observable<Array<UnreadMessageDto>> {
    return this.httpClient.post('/ApplicationCase/Search', this.getCasesSearchOptions()).pipe(
      map((response: CaseSearchResponseResult<ExternalCaseSearchResultDto>) => {
        this.numberOfAlertedCases$.next(response.Count);

        return response.Data.filter((dto) => dto.AlertsCount > 0).map((dto) => this.caseSearchDtoToUnreadMessage(dto));
      })
    );
  }

  private getCasesSearchOptions(): BaseFilterOptions<ApplicationCaseAdvancedFilterOptions> {
    const options = new BaseFilterOptions<ApplicationCaseAdvancedFilterOptions>();
    options.AdvancedFilters.ShowOnlyAlertedCases = true;
    options.GridOptions.Skip = 0;
    options.GridOptions.Take = this.maximumUnreadMessagesCases;

    return options;
  }

  private caseSearchDtoToUnreadMessage(caseSearchDto: ExternalCaseSearchResultDto): UnreadMessageDto {
    const unreadMessage = new UnreadMessageDto();
    unreadMessage.AlertsCount = caseSearchDto.AlertsCount;
    unreadMessage.CaseID = caseSearchDto.CaseIDOriginal;
    unreadMessage.CaseIDHash = caseSearchDto.CaseID;
    unreadMessage.CaseType = caseSearchDto.CaseType;
    unreadMessage.SamID = caseSearchDto.SamID;

    return unreadMessage;
  }

  private buildUnreadMessagesObservable(): Observable<Array<UnreadMessageDto>> {
    const notificationsChanged$ = this.notificationsAvailable$.pipe(
      filter((check) => {
        return check.ShouldRefreshMessageNotification || this.numberOfCasesAssigned !== check.CasesAssigned;
      })
    );

    return combineLatest([notificationsChanged$, this.refresh$]).pipe(
      map(([check, _]) => check),
      switchMap((check) => {
        this.numberOfCasesAssigned = check.CasesAssigned;

        return this.getUnreadMessages();
      }),
      shareReplay(1)
    );
  }

  private buildNotificationsAvailableObservable(notificationRefreshRate: number): Observable<NotificationCheckDto> {
    return timer(0, notificationRefreshRate).pipe(
      takeUntil(this.stop$),
      switchMap(() => this.isNotificationAvailable(this.latestCheckTime)),
      tap((check) => {
        this.latestCheckTime = check.NotificationCheckTime;
      }),
      share()
    );
  }
}
